/**
 * Shared "is this a real signed-in user?" guard.
 *
 * The `/api/user/me` endpoint returns the full `User` document on success, but a
 * stale/invalid session can instead yield an error body (`{ message }`), an HTML
 * error page, `true`/`null`, or an empty object. Treating any of those as a
 * logged-in user leaves the app in a broken half-authenticated state.
 *
 * A genuine user payload always carries a stable `_id` plus at least one identity
 * field (`email` or a `roles` array). Anything missing those is not a valid user
 * and must trigger a full logout.
 *
 * @param {*} candidate Raw value returned from `/me` (already JSON-parsed).
 * @returns {boolean} True only when `candidate` is a usable user object.
 */
export function isValidUserObject(candidate) {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
        return false;
    }

    const id = candidate._id || candidate.id;
    if (!id || typeof id !== 'string') {
        return false;
    }

    return Boolean(candidate.email || Array.isArray(candidate.roles));
}

/**
 * Normalize the various shapes a networking layer can wrap a user payload in
 * (`{ body }`, `{ obj }`, `{ data }`, or the bare object) down to the user.
 *
 * @param {*} response Raw networking response / parsed body.
 * @returns {*} The most likely user object inside `response`.
 */
export function unwrapUserPayload(response) {
    if (!response || typeof response !== 'object') {
        return response;
    }
    return response.user || response.body || response.obj || response.data || response;
}

/**
 * Whether the user's primary restaurant has had its system-access rights revoked
 * mid-session (`customerStatus === 'rejected'` — the "System usage rights
 * revoked!" state). Mirrors the backend `checkRestaurantAccessRevoked`: the
 * PRIMARY restaurant (`restaurants[0]`) drives the gate and platform operators
 * (`system:admin`) are never locked out.
 *
 * @param {*} user A validated user object (see {@link isValidUserObject}).
 * @returns {boolean}
 */
export function isRestaurantAccessRevoked(user) {
    if (!user || typeof user !== 'object') {
        return false;
    }

    const roles = Array.isArray(user.roles) ? user.roles : [];
    if (roles.includes('system:admin')) {
        return false;
    }

    const restaurants = Array.isArray(user.restaurants) ? user.restaurants : [];
    const primaryRestaurant = restaurants[0];

    return !!primaryRestaurant && primaryRestaurant.customerStatus === 'rejected';
}

export default isValidUserObject;
